import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  ArrowRightLeft, 
  CreditCard, 
  Bot, 
  LogOut, 
  Bell, 
  Search, 
  Menu, 
  X,
  Wallet
} from './Icons';
import { ViewState, Language } from '../types';
import { translations } from '../utils/translations';

interface DashboardLayoutProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  children: React.ReactNode;
  onLogout: () => void;
  language: Language;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ currentView, setView, children, onLogout, language }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const t = translations[language].dashboard;

  const navItems = [
    { id: ViewState.DASHBOARD, label: t.nav.home, icon: LayoutDashboard },
    { id: ViewState.TRANSACTIONS, label: t.nav.transactions, icon: ArrowRightLeft },
    { id: ViewState.CARDS, label: t.nav.cards, icon: CreditCard },
    { id: ViewState.AI_ASSISTANT, label: t.nav.ai, icon: Bot, isPremium: true },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-brand-dark text-white">
      <div className="p-6 flex items-center gap-3">
        <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center">
          <span className="text-brand-dark font-bold text-lg">B</span>
        </div>
        <span className="text-xl font-bold tracking-wide">Banco<span className="text-brand-green">WEB</span></span>
      </div>

      <nav className="flex-1 px-4 py-4 space-y-2">
        {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
                <button
                    key={item.id}
                    onClick={() => {
                        setView(item.id);
                        setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                        isActive 
                        ? 'bg-brand-blue text-white shadow-lg shadow-blue-900/50' 
                        : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-brand-lime' : 'group-hover:text-brand-lime'}`} />
                    <span className="font-medium">{item.label}</span>
                    {item.isPremium && (
                         <span className="ml-auto text-[10px] uppercase font-bold bg-brand-lilac text-brand-purple px-1.5 py-0.5 rounded">Beta</span>
                    )}
                </button>
            )
        })}
      </nav>

      <div className="p-4 border-t border-white/10">
        <button 
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-red-500/10 hover:text-red-400 rounded-xl transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">{t.nav.signout}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="fixed inset-y-0 left-0 w-64 shadow-2xl animate-slide-in">
             <SidebarContent />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
                <button 
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                    <Menu className="w-6 h-6" />
                </button>
                <div className="hidden sm:flex items-center bg-slate-100 rounded-full px-4 py-2 w-64 border border-transparent focus-within:border-brand-blue focus-within:bg-white transition-all">
                    <Search className="w-4 h-4 text-slate-400 mr-2" />
                    <input 
                        type="text" 
                        placeholder={t.header.search}
                        className="bg-transparent border-none outline-none text-sm w-full placeholder-slate-400 text-slate-700"
                    />
                </div>
            </div>

            <div className="flex items-center gap-4">
                <button className="relative p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors">
                    <Bell className="w-5 h-5" />
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                    <div className="text-right hidden sm:block">
                        <p className="text-sm font-bold text-slate-900">Alex Merchant</p>
                        <p className="text-xs text-slate-500">{t.header.pro}</p>
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-brand-blue to-brand-purple rounded-full flex items-center justify-center text-white font-bold shadow-md ring-2 ring-white cursor-pointer">
                        AM
                    </div>
                </div>
            </div>
        </header>

        {/* View Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                 {children}
            </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;